/**********************************************************************
// @@@ START COPYRIGHT @@@
//        AVI SOLAR ENERGY CONFIDENTIAL: NEED TO KNOW ONLY
//        Copyright 2018 - 2019
//        Avi Solar Energy Pvt Ltd.
//        Protected as an unpublished work.
//  The computer program listings, specifications and documentation
//  herein are the property of Avi Solar Energy Pvt Ltd., or a third party supplier
//  and shall not be reproduced, copied, disclosed, or used in whole or in part for
//  any reason without the prior express written permission of Avi Solar Energy Pvt Ltd.
// @@@ END COPYRIGHT @@@
// +++ Code modified on 2019/02/25
**********************************************************************/
<template>
  <Card class="cardClass">
    <Toolbar>
      <v-toolbar-title class="fs-16">{{ $t('Daily Updates') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <TextInput :value="search" :placeholder="$t('Search')" @change="(data) => search = data"></TextInput>
      <AvatarButton icon="cached" :tip="$t('Refresh')" @click="refresh"></AvatarButton>
      <AvatarButton icon="get_app" :tip="$t('Download CSV')" class="hidden-sm-and-down" @click="download">
      </AvatarButton>
    </Toolbar>
    <v-layout column>
      <MobileList :data="tableData" v-if="isMobile"></MobileList>
      <v-data-table class="elevation-1 dataTable" fixed-header :headers="headers" :items="tableData"
        :search="search" hide-actions :loading="dataLoading" :custom-sort="sortByNum">
        <template v-slot:items="props">
          <tr @click="selectPlant(props.item.plant_id, props.item.plant_name)">
            <td>
              {{ props.item.plant_name }} </td>
            <td>
              {{ getGeneralState('plantData')[props.item.plant_id].city }}</td>
            <td>
              {{ props.item.location }}</td>
            <td style="text-align:center" :class="{'error--text': !props.item.generation}">
              {{ props.item.generation != null ? props.item.generation : $t("No data") }}</td>
            <td style="text-align:center" :class="{'error--text': !props.item.power}">
              {{ props.item.power != null ? props.item.power : $t("No data") }}</td>
            <td style="text-align:center" :class="{'error--text': !props.item.acyield}">
              {{ props.item.acyield != null ? props.item.acyield : $t("No data") }}</td>
            <td xs12 sm12 md12 lg12 xl12>
              <span v-for="value, key in props.item.alert" v-bind:key="key">
                <v-tooltip top>
                  {{value}}
                  <v-btn v-bind:class="value" fab small slot="activator"
                    class="fab-btn-xs alert-indicator"
                    v-on:click="redirectAlert(props.item.plant_id, alertShorthand[key])">
                    {{alertShorthand[key]}}
                  </v-btn>
                  <span>{{key.toUpperCase()}}</span>
                  <br />
                  <span v-if="key == 'communication'">
                    <span>Last Alert: </span>
                    <span style="font-size: 1.2em;"> {{props.item.latestAlerts[key] || '-'}} </span>
                  </span>
                </v-tooltip>
              </span>
            </td>
            <td style="text-align:center">
              {{ props.item.insolation }}</td>
            <td style="text-align:center"  id="ratio">
              <span v-if="props.item.ratio=='N/A'">N/A</span>
              <span v-else-if="props.item.ratio>100">---</span>
              <span v-else>{{props.item.ratio | round}}</span>
            </td>
          </tr>
        </template>
        <v-alert id="search" slot="no-results" :value="true" color="error" icon="warning">
          {{ $t('Your search for') }} "{{ search }}" {{ $t('found no results') }}
        </v-alert>
      </v-data-table>
      <div class="legend-background">
        <v-btn class="legend" xs3 sm3 md3 lg3 xl3 color="error" fab small></v-btn>
        <span>{{ $t('New Alerts') }}</span>
        <v-btn class="legend" xs3 sm3 md3 lg3 xl3 color="success" fab small></v-btn>
        <span>{{ $t('No Alerts') }}</span>
      </div>
    </v-layout>
  </Card>
</template>

<script>
import {
  mapGetters,
  mapMutations,
  mapActions
} from 'vuex'
import Home from '@/services/Home'
import TextInput from '@/components/home/TextInput.vue'
import AvatarButton from '@/components/home/AvatarButton.vue'
import MobileList from '@/components/home/DataTable/MobileList.vue'

import {
  mixin,
  mapGettersMixin
} from '@/util/mixin/index'
import moment from 'moment'

export default {
  mixins: [ mixin, mapGettersMixin ],
  components: {
    TextInput,
    AvatarButton,
    MobileList
  },
  data() {
    return {
      alertShorthand: {
        inverter: 'I',
        grid: 'G',
        scb: 'S',
        communication: 'C',
        transformer: 'T',
        computed: 'P'
      },
      pagination: {
        rowsPerPage: 15
      },
      search: '',
      page: 1,
      tableData: [],
      param: {
        plantList: []
      },
      dataLoading: true,
      timer: '',
      alert: {
        G: 'grid',
        I: 'inverter',
        C: 'communication',
        T: 'transformer',
        P: 'PR'
      }
    }
  },
  mounted() {
    this.setIntervalWithoutDelay(this.callAllApi)
  },
  computed: {
    ...mapGetters([
      'getPlantName',
      'getGeneralState',
      'getPlantList',
      'getPlantId'
    ]),
    indicator() {
      return this.alert ? 'error blink' : 'success'
    },
    plantsSearched() {
      return this.getPlantList.filter(el => {
        return el.toLowerCase().includes(this.search.toLowerCase())
      }).map(el => this.getPlantId(el))
    },
    isMobile() {
      switch (this.$vuetify.breakpoint.name) {
        case 'xs':
        case 'sm':
          return true
        case 'md':
        case 'lg':
        case 'xl':
          return false
      }
    },
    headers() {
      var head = [{
        text: this.$t('Name'),
        align: 'left',
        value: 'plant_name'
      },
      {
        text: this.$t('City'),
        value: 'city'
      },
      {
        text: this.$t('State'),
        value: 'location'
      },
      {
        text: this.$t('Gen (kWh)'),
        value: 'generation',
        align: 'center'
      },
      {
        text: this.$t('AC Power (kW)'),
        value: 'power',
        align: 'center'
      },
      {
        text: this.$t('Yield (kWh/kWp)'),
        value: 'acyield',
        align: 'center'
      },
      {
        text: this.$t('Status'),
        sortable: false,
        value: 'alert',
        width: '180px',
        align: 'center'
      },
      {
        text: this.$t('Insolation'),
        value: 'insolation',
        align: 'center'
      },
      {
        text: this.$t('PR (%)'),
        value: 'ratio',
        align: 'center'
      }
      ]
      return head
    },
    plantListForCurrentPage() {
      if (this.search === '') {
        return this.getPlantList.map(el => el.id)
      } else {
        return this.plantsSearched
      }
    }
  },
  watch: {
    isReload() {
      if (this.isReload) {
        this.setIntervalWithoutDelay(this.callAllApi)
      }
    },
    page() {
      this.getGenerationDetails()
    },
    search() {
      clearTimeout(this.timer)
      this.timer = setTimeout(() => {
        this.page = 1
        this.debounce()
      }, 500)
    }
  },
  methods: {
    ...mapActions(['fetchGenerationData', 'fetchActivePlantDetails']),
    ...mapMutations([
      'SET_TOTAL_GENERATION',
      'SET_PLANT_TOTAL_GENERATION',
      // 'SET_SELECTED_PLANT_ID',
      'SET_SELECTED_COMPONENT_MENU',
      'RESET_STATE',
      'SET_LAST_CALL_TIME',
      'SET_TOTAL_PR',
      'SET_PROGRESS_BAR'
    ]),

    sortByNum(item, index, isDesc) {
      function numericCompare(a, b) {
        if (isDesc === true) {
          if (parseFloat(a[index].toString().replace(/,/gi, '')) < parseFloat(b[index].toString().replace(/,/gi, ''))) {
            return -1
          }
          if (parseFloat(a[index].toString().replace(/,/gi, '')) > parseFloat(b[index].toString().replace(/,/gi, ''))) {
            return 1
          }
        } else if (isDesc === false) {
          if (parseFloat(a[index].toString().replace(/,/gi, '')) > parseFloat(b[index].toString().replace(/,/gi, ''))) {
            return -1
          }
          if (parseFloat(a[index].toString().replace(/,/gi, '')) < parseFloat(b[index].toString().replace(/,/gi, ''))) {
            return 1
          }
        }
        return 0
      }

      function stringCompare(a, b) {
        if (isDesc === true) {
          if (a[index] < b[index]) {
            return -1
          }
          if (a[index] > b[index]) {
            return 1
          }
        } else if (isDesc === false) {
          if (a[index] > b[index]) {
            return -1
          }
          if (a[index] < b[index]) {
            return 1
          }
        }
        return 0
      }

      function prSort(a, b) {
        if (isDesc === true) {
          if (typeof a[index] === typeof b[index]) {
            return a[index] < b[index] ? -1 : 1
          }
          return typeof a[index] > typeof b[index] ? -1 : 1
        } else if (isDesc === false) {
          if (typeof a[index] === typeof b[index]) {
            return a[index] > b[index] ? -1 : 1
          }
          return typeof a[index] < typeof b[index] ? -1 : 1
        }
        return 0
      }
      if (index === 'generation') {
        return item.sort(numericCompare)
      } else if (index === 'plant_name') {
        return item.sort(stringCompare)
      } else if (index === 'city') {
        return item.sort(stringCompare)
      } else if (index === 'location') {
        return item.sort(stringCompare)
      } else if (index === 'ratio') {
        return item.sort(prSort)
      } else if (index === 'insolation') {
        return item.sort(numericCompare)
      } else if (index === 'power') {
        return item.sort(numericCompare)
      } else if (index === 'acyield') {
        return item.sort(numericCompare)
      } else {
        return item
      }
    },

    async debounce() {
      await this.getGenerationDetails()
    },
    callAllApi() {
      let updateTime = moment().format('MM-DD-YYYY H:mm:ss')
      this.SET_LAST_CALL_TIME(updateTime)
      this.getGenerationDetails()
    },
    async redirectAlert(plantId) {
      await this.fetchActivePlantDetails(plantId)
      this.$router.push({
        'name': 'alert'
      })
    },
    async getGenerationDetails() {
      if (this.plantListForCurrentPage.length === 0) {
        this.tableData.splice(0)
        return
      }

      this.dataLoading = true
      const param = {
        plantList: this.plantListForCurrentPage
      }
      this.SET_PROGRESS_BAR(true)
      try {
        let response = await Home.dashboardData(param)
        this.SET_PROGRESS_BAR(false)
        let res = response.data.message
        let totalGeneration = 0
        this.tableData = []
        let count = 0
        let totalPr = 0
        Object.values(res).forEach(element => {
          let plantDetails = Object.assign({}, element)
          if (element.generation) {
            totalGeneration += parseFloat(element.generation)
            plantDetails.generation = element.generation
          }
          plantDetails.location = this.getGeneralState('plantData')[element.plant_id].state
          plantDetails.plant_name = this.getPlantName(element.plant_id)

          if (element.ratio && element['ratio'] < 100 && element['ratio'] !== 0 && !isNaN(element['ratio'])) {
            totalPr += parseInt(element['ratio'])
            count++
          }
          this.$set(this.tableData, this.tableData.length, plantDetails)
        })
        this.SET_TOTAL_PR((totalPr / count).toFixed(2))

        this.SET_PLANT_TOTAL_GENERATION(totalGeneration)
        this.SET_TOTAL_GENERATION(totalGeneration)
        this.dataLoading = false
      } catch (err) {
        this.SET_PROGRESS_BAR(false)
      }
    },
    refresh() {
      this.tableData.length = 0
      this.setIntervalWithoutDelay(this.callAllApi)
    },
    async selectPlant(item) {
      await this.fetchActivePlantDetails(item)
      this.SET_SELECTED_COMPONENT_MENU('plantOverview')
      this.$router.push('/plantOverview')
    },
    download() {
      if (this.tableData.length === 0) {} else {
        for (let i = 0; i < this.tableData.length; i++) {
          this.tableData[i][`grid`] = this.tableData[i].alert
            ? this.tableData[i].alert.grid
              ? this.tableData[i].alert.grid
              : 'NA'
            : 'NA'
          this.tableData[i][`communication`] = this.tableData[i].alert
            ? this.tableData[i].alert.communication
              ? this.tableData[i].alert.communication
              : 'NA'
            : 'NA'
          this.tableData[i][`inverter`] = this.tableData[i].alert
            ? this.tableData[i].alert.inverter
              ? this.tableData[i].alert.inverter
              : 'NA'
            : 'NA'
          this.tableData[i][`scb`] = this.tableData[i].alert
            ? this.tableData[i].alert.scb
              ? this.tableData[i].alert.scb
              : 'NA'
            : 'NA'
          if (this.tableData[i].ratio >= 100) {
            this.tableData[i].ratio = '--'
          }
        }
        this.JSONToCSVConvertor(this.tableData, 'Plant Report', false)
      }
    },
    JSONToCSVConvertor(JSONData, ReportTitle, ShowLabel) {
      // If JSONData is not an object then JSON.parse will parse the JSON string in an Object
      var arrData =
        typeof JSONData !== 'object' ? JSON.parse(JSONData) : JSONData

      var CSV = ''
      // Set Report title in first row or line

      let header =
        'PLANT_NAME, LOCATION, GENERATION, PR, Grid_alert,Communication_alert,Inverter_alert,SCB_alert'
      let headerOrder = [
        'plant_name',
        'location',
        'generation',
        'ratio',
        'grid',
        'communication',
        'inverter',
        'scb'
      ]
      // This condition will generate the Label/Header
      if (ShowLabel) {
        let row = ''

        // This loop will extract the label from 1st index of on array
        for (let index in arrData[0]) {
          // Now convert each value to string and comma-seprated
          row += index + ','
        }
        row = row.slice(0, -1)

        // append Label row with line break
        CSV += row + '\r\n'
      }
      CSV += header + '\r\n'

      // 1st loop is to extract each row
      for (var i = 0; i < arrData.length; i++) {
        let row = ''
        // 2nd loop will extract each column and convert it in string comma-seprated
        for (var index in headerOrder) {
          row += '"' + arrData[i][headerOrder[index]] + '",'
        }
        row.slice(0, row.length - 1)

        // add a line break after each row
        CSV += row + '\r\n'
      }

      if (CSV === '') {
        alert('Invalid data')
        return
      }

      // Generate a file name
      var fileName = 'MyReport_'
      // this will remove the blank-spaces from the title and replace it with an underscore
      fileName += ReportTitle.replace(/ /g, '_')

      // Initialize file format you want csv or xls
      var uri = 'data:text/csv;charset=utf-8,' + escape(CSV)

      // Now the little tricky part.
      // you can use either>> window.open(uri);
      // but this will not work in some browsers
      // or you will not get the correct file extension

      // this trick will generate a temp <a /> tag
      var link = document.createElement('a')
      link.href = uri

      // set the visibility hidden so it will not effect on your web-layout
      link.style = 'visibility:hidden'
      link.download = fileName + '.csv'

      // this part will append the anchor tag and remove it after automatic click
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}
</script>

<style scoped>
.dataTable {
  height: calc(100vh - 305px);
  overflow: auto;
  display: flex;
}

.no_alert {
  background-color: #61B865 !important;
  color: white;
}

.alert-indicator {
  height: 22px;
  width: 22px
}

.alert {
  animation: blinker 1s linear infinite;
  background-color: #E14C50 !important;
  color: white !important;
}

input {
  outline: none;
  margin: 6px;
}

input {
  -webkit-appearance: textfield;
  box-sizing: content-box;
  -webkit-box-sizing: content-box;
  font-family: inherit;
  font-size: 14px;
}

input::-webkit-search-decoration,
input::-webkit-search-cancel-button {
  display: none;
}

input {
  padding: 5px 20px 5px 10px;
  width: 70px;

  -webkit-border-radius: 10em;
  -moz-border-radius: 10em;
  border-radius: 10em;

  -webkit-transition: all 0.4s;
  -moz-transition: all 0.4s;
  transition: all 0.4s;
}

input:focus {
  width: 130px;
  /* -webkit-box-shadow: 0 0 5px rgba(109, 207, 246, 0.5);
    -moz-box-shadow: 0 0 5px rgba(109, 207, 246, 0.5);
    box-shadow: 0 0 5px rgba(109, 207, 246, 0.5); */
}

.legend {
  height: 12px !important;
  width: 12px !important;
  position: relative;
}

.right-space {
  margin-right: 10px;
}

.blink {
  animation: blinker 1s linear infinite;
  /* background-color: red !important; */
  /* color: white !important; */
}

ack,
.ack {
  background-color: yellow !important;
  color: black !important;
}

.fab-btn-xs {
  height: 20px;
  width: 20px;
}

/* Theme */

.theme--dark input {
  background: url(../../../assets/baseline-search-light.svg) no-repeat right center;
}

.theme--light input {
  background: url(../../../assets/baseline-search-light.svg) no-repeat right center;
  border: solid 1px;
}

.avatar-bg {
  background-color: transparent;
  border-color: var(--v-primary-base);
  border-width: 2px;
  border-style: solid;
}

.theme--light .avatar-bg {
  /* border-color: lightgray; */
  border-width: 1px;
  border-style: solid;
}

</style>

<style>
.dataTable th {
  z-index: 1;
  position: sticky;
  top: var(--v-extraGrey) !important;
  background: var(--v-primary-base) !important;
}

.dataTable .v-table__overflow {
  overflow: auto
}

.con-tooltip {
  /* #b27221 */
  /* background: #E9952A; */
  background: #cc535673;
  cursor: pointer;
}

.renew-msg {
  margin-bottom: 6px;
  margin-top: 6px;
}

/*tooltip */
.tooltip {
  visibility: hidden;
  z-index: 1;
  opacity: 0.70;

  width: 20%;
  padding: 0px 20px;
  border: 2px solid #cc535673;
  background: #cc535673;
  color: white;

  position: absolute;
  top: -149%;
  left: 15%;

  border-radius: 9px;
  font: 16px;

  transform: translateY(9px);
  transition: all 0.3s ease-in-out;

  box-shadow: 0 0 3px rgba(56, 54, 54, 0.86);
}

/* tooltip  after*/
.tooltip::after {
  content: " ";
  width: 0;
  height: 0;

  border-style: solid;
  border-width: 12px 12.5px 0 12.5px;
  border-color: #cc5356 transparent transparent transparent;

  position: absolute;
  left: 40%;

}

.con-tooltip:hover .tooltip {
  visibility: visible;
  transform: translateY(-10px);
  /* opacity: 1; */
  transition: .3s linear;
  animation: odsoky 1s ease-in-out infinite alternate;
}

@keyframes odsoky {
  0% {
    transform: translateY(6px);
  }

  100% {
    transform: translateY(1px);
  }

}

/*hover ToolTip*/
.top:hover {
  transform: translateY(0px);
}

.legend-background {
  /* background-color: lightgray; */
  padding: 4px;
  margin-top: 17px;
}

.legend-background span {
  /* color: black; */
  font-size: 14px;
}

/* table.v-table thead td:not(:nth-child(1)),
table.v-table tbody td:not(:nth-child(1)),
table.v-table thead th:not(:nth-child(1)),
table.v-table tbody th:not(:nth-child(1)),
table.v-table thead td:first-child,
table.v-table tbody td:first-child,
table.v-table thead th:first-child,
table.v-table tbody th:first-child {
  padding: 0px 0px 0px 20px !important;
} */

</style>
